import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildOAuthLoginUrl } from '../services/api';
import { useTranslation } from 'react-i18next';
import './LandingPage.css';

interface TestimonialItem {
  text: string;
  name: string;
  position: string;
  icon: string;
}

interface FaqItem {
  q: string;
  a: string;
}

function ProductVideo({ id, title, description, file }: { id: string; title: string; description: string; file: string }) {
  return (
    <div id={id} className="product-video">
      <div className="product-video-heading">
        <div><h3>{title}</h3><p>{description}</p></div>
        <span className="product-video-duration">23 секунды</span>
      </div>
      <video controls playsInline preload="none" poster={`/media/product/${file}.jpg`} aria-label={title}>
        <source src={`/media/product/${file}.mp4`} type="video/mp4" />
        Ваш браузер не поддерживает видео. <a href={`/media/product/${file}.mp4`}>Скачать ролик</a>
      </video>
    </div>
  );
}

// Mini interactive spreadsheet demo for the landing page
const DEMO_INIT = [
  ['', 'Jan', 'Feb', 'Mar', 'Q1 Total'],
  ['Revenue', 12500, 14200, 13800, 0],
  ['Expenses', 8300, 9100, 8700, 0],
  ['Profit', 0, 0, 0, 0],
  ['Margin %', '', '', '', ''],
];

function recalcDemo(grid: (string | number)[][]): (string | number)[][] {
  const g = grid.map(r => [...r]);
  // Q1 totals (col 4 = sum of cols 1-3)
  for (let r = 1; r <= 2; r++) {
    g[r][4] = (Number(g[r][1]) || 0) + (Number(g[r][2]) || 0) + (Number(g[r][3]) || 0);
  }
  // Profit = Revenue - Expenses
  for (let c = 1; c <= 4; c++) {
    g[3][c] = (Number(g[1][c]) || 0) - (Number(g[2][c]) || 0);
  }
  // Margin % = Profit / Revenue
  for (let c = 1; c <= 4; c++) {
    const rev = Number(g[1][c]) || 0;
    g[4][c] = rev > 0 ? (((Number(g[3][c]) || 0) / rev) * 100).toFixed(1) + '%' : '—';
  }
  return g;
}

function fmtNum(v: string | number): string {
  if (typeof v === 'number') return v.toLocaleString('en-US');
  return String(v);
}

const DemoSpreadsheet: React.FC = () => {
  const [cells, setCells] = useState<(string | number)[][]>(() => recalcDemo(DEMO_INIT));
  const [editing, setEditing] = useState<{ r: number; c: number } | null>(null);

  const handleCellClick = (r: number, c: number) => {
    if (r === 0 || c === 0) return;
    if (r >= 3) return; // Profit/Margin are computed
    if (c === 4) return; // Totals are computed
    setEditing({ r, c });
  };

  const handleCellChange = (r: number, c: number, value: string) => {
    setCells(prev => {
      const next = prev.map(row => [...row]);
      next[r][c] = value === '' ? 0 : (isNaN(Number(value)) ? value : Number(value));
      return recalcDemo(next);
    });
  };

  const handleBlur = () => setEditing(null);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Escape') setEditing(null);
  };

  return (
    <div className="demo-spreadsheet">
      <table>
        <tbody>
          {cells.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => {
                const isHeader = r === 0 || c === 0;
                const isEditing = editing?.r === r && editing?.c === c;
                const isComputed = (r >= 3) || (c === 4 && r > 0);
                return (
                  <td
                    key={c}
                    className={`demo-cell${isHeader ? ' demo-header' : ''}${isComputed ? ' demo-formula' : ''}`}
                    onClick={() => handleCellClick(r, c)}
                    title={isComputed && !isHeader ? 'Computed' : undefined}
                  >
                    {isEditing ? (
                      <input
                        className="demo-cell-input"
                        value={String(cell)}
                        onChange={e => handleCellChange(r, c, e.target.value)}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        autoFocus
                      />
                    ) : (
                      fmtNum(cell)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation('landing');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [navScrolled, setNavScrolled] = useState(false);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const testimonials = t('testimonials.items', { returnObjects: true }) as TestimonialItem[];
  const faqItems = t('faq.items', { returnObjects: true }) as FaqItem[];

  const handleLoginClick = () => {
    if (isAuthenticated) {
      navigate('/reports');
    } else {
      navigate('/login');
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    autoplayRef.current = setInterval(nextSlide, 5000);
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [nextSlide]);

  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 50);

      const animatedElements = document.querySelectorAll('.fade-in-up, .timeline-item, .faq-item');
      animatedElements.forEach((element) => {
        const rect = (element as HTMLElement).getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.85) {
          element.classList.add('visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="landing-page">
      {/* Sticky Navigation */}
      <nav className={`landing-nav ${navScrolled ? 'scrolled' : ''}`}>
        <div className="landing-nav-inner">
          <div className="landing-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="/static/logo-ai.png" alt="AI Reports" className="landing-nav-logo-img" />
            <span>AI Reports</span>
          </div>
          <div className="landing-nav-links">
            <Link to="/editor" className="landing-nav-link-editor">{t('nav.editor')}</Link>
            <Link to="/templates" className="landing-nav-link-templates">{t('templates.page_title', 'Templates')}</Link>
            <button onClick={() => scrollToSection('how-it-works')}>{t('nav.how_it_works')}</button>
            <button onClick={() => scrollToSection('pricing')}>{t('nav.pricing')}</button>
            <button onClick={() => scrollToSection('faq')}>{t('nav.faq')}</button>
            <button onClick={() => window.location.href = '/blog'}>{t('nav.blog')}</button>
          </div>
          <button onClick={handleLoginClick} className="landing-nav-cta">
            {isAuthenticated ? t('nav.cta_auth') : t('nav.cta_guest')}
          </button>
        </div>
      </nav>

      {/* Hero — Split: Editor + Reports */}
      <header>
        <div className="hero-content">
          <h1 dangerouslySetInnerHTML={{ __html: t('hero.title') }} />
          <p className="hero-subtitle">{t('hero.subtitle')}</p>

          <div className="hero-split">
            {/* Left: Excel Editor */}
            <div className="hero-card hero-card-editor">
              <div className="hero-card-icon"><i className="fas fa-table"></i></div>
              <h2>{t('hero.editor_title', 'Online Excel Editor')}</h2>
              <p>{t('hero.editor_desc', 'Create and edit spreadsheets in your browser with an AI assistant')}</p>
              <Link to="/editor" className="hero-card-btn hero-card-btn-editor">
                <i className="fas fa-external-link-alt"></i> {t('hero.cta_primary')}
              </Link>
              {i18n.language === 'ru' && <button className="hero-video-link" onClick={() => scrollToSection('excel-ai-video')}>▶ Смотреть видео · 23 сек</button>}
              <div className="hero-card-features">
                <span><i className="fas fa-check"></i> {t('hero.micro_1')}</span>
                <span><i className="fas fa-check"></i> {t('hero.micro_2')}</span>
                <span><i className="fas fa-check"></i> {t('hero.micro_3')}</span>
              </div>
            </div>

            {/* Right: AI Reports */}
            <div className="hero-card hero-card-reports">
              <div className="hero-card-icon"><i className="fas fa-chart-bar"></i></div>
              <h2>{t('hero.reports_title', 'AI Reports from Excel')}</h2>
              <p>{t('hero.reports_desc', 'Upload a file — get a finished report with charts and conclusions in 2 minutes')}</p>
              <button onClick={handleLoginClick} className="hero-card-btn hero-card-btn-reports">
                <i className="fas fa-rocket"></i> {isAuthenticated ? t('nav.cta_auth') : t('hero.reports_cta', 'Try for free')}
              </button>
              {i18n.language === 'ru' && <button className="hero-video-link" onClick={() => scrollToSection('reports-video')}>▶ Смотреть видео · 23 сек</button>}
              <div className="hero-card-features">
                <span><i className="fas fa-check"></i> {t('hero.reports_f1', '1,000 free credits')}</span>
                <span><i className="fas fa-check"></i> {t('hero.reports_f2', 'Word & PDF export')}</span>
                <span><i className="fas fa-check"></i> {t('hero.reports_f3', '3 LLM providers')}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="wave-divider">
          <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="shape-fill"></path>
          </svg>
        </div>
      </header>

      {/* Stats Bar — social proof */}
      <div className="stats-bar">
        <div className="stats-bar-inner">
          <div className="stat-item fade-in-up">
            <div className="stat-number">{t('stats.reports_count')}</div>
            <div className="stat-label">{t('stats.reports_label')}</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item fade-in-up">
            <div className="stat-number">{t('stats.users_count')}</div>
            <div className="stat-label">{t('stats.users_label')}</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item fade-in-up">
            <div className="stat-number">{t('stats.time_count')}</div>
            <div className="stat-label">{t('stats.time_label')}</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item fade-in-up">
            <div className="stat-number">{t('stats.editor_count')}</div>
            <div className="stat-label">{t('stats.editor_label')}</div>
          </div>
        </div>
      </div>

      {/* Editor Features — new section */}
      <section>
        <div className="editor-features-section fade-in-up">
          <h2>{t('editor_features.title')}</h2>
          <p className="section-subtitle">{t('editor_features.subtitle')}</p>
          <div className="output-grid editor-grid">
            {[1, 2, 3, 4].map(n => (
              <div className="output-card editor-card" key={n}>
                <div className="output-card-icon"><i className={t(`editor_features.item${n}_icon`)}></i></div>
                <h3>{t(`editor_features.item${n}_title`)}</h3>
                <p>{t(`editor_features.item${n}_text`)}</p>
              </div>
            ))}
          </div>
          {i18n.language === 'ru' && <ProductVideo id="excel-ai-video" title="Excel + ИИ: от вопроса к результату" description="Посмотрите, как ИИ помогает работать с таблицей прямо в редакторе." file="excel-ai-20260930" />}
          {/* Interactive mini-demo */}
          <div className="demo-editor fade-in-up">
            <div className="demo-editor-header">
              <div className="demo-editor-dots">
                <span className="demo-dot red"></span>
                <span className="demo-dot yellow"></span>
                <span className="demo-dot green"></span>
              </div>
              <span className="demo-editor-title">{t('editor_features.demo_title', 'Try it — click a cell to edit')}</span>
            </div>
            <DemoSpreadsheet />
          </div>

          <div className="editor-features-cta">
            <Link to="/editor" className="hero-btn-primary">
              <i className="fas fa-external-link-alt"></i>
              {t('hero.cta_primary')}
            </Link>
          </div>
        </div>
      </section>

      {/* Bridge — Editor → Reports synergy */}
      <div id="bridge" className="bridge-section">
        <div className="bridge-inner">
          <div className="bridge-badge"><i className="fas fa-link"></i> {t('bridge.badge')}</div>
          <h2>{t('bridge.title')}</h2>
          <p className="bridge-subtitle">{t('bridge.subtitle')}</p>
          <div className="bridge-steps">
            <div className="bridge-step">
              <div className="bridge-step-icon"><i className="fas fa-edit"></i></div>
              <span>{t('bridge.step1')}</span>
            </div>
            <div className="bridge-arrow"><i className="fas fa-arrow-right"></i></div>
            <div className="bridge-step">
              <div className="bridge-step-icon"><i className="fas fa-magic"></i></div>
              <span>{t('bridge.step2')}</span>
            </div>
            <div className="bridge-arrow"><i className="fas fa-arrow-right"></i></div>
            <div className="bridge-step">
              <div className="bridge-step-icon"><i className="fas fa-file-alt"></i></div>
              <span>{t('bridge.step3')}</span>
            </div>
          </div>
          <button onClick={() => scrollToSection('how-it-works')} className="bridge-cta">
            {t('bridge.cta')} <i className="fas fa-chevron-down"></i>
          </button>
        </div>
      </div>

      <section>
        {/* How it works — 3 steps */}
        <div id="how-it-works" className="steps">
          <h2>{t('how_it_works.title')}</h2>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-content">
                <div className="step-number">1</div>
                <h3><i className="fas fa-file-upload"></i> {t('how_it_works.step1_title')}</h3>
                <p>{t('how_it_works.step1_text')}</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-content">
                <div className="step-number">2</div>
                <h3><i className="fas fa-robot"></i> {t('how_it_works.step2_title')}</h3>
                <p>{t('how_it_works.step2_text')}</p>
                <div className="tick-counter">
                  <i className="fas fa-cog fa-spin"></i>
                  <span>{t('how_it_works.step2_cost')}</span>
                </div>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-content">
                <div className="step-number">3</div>
                <h3><i className="fas fa-file-download"></i> {t('how_it_works.step3_title')}</h3>
                <p>{t('how_it_works.step3_text')}</p>
              </div>
            </div>
          </div>
        </div>

        {i18n.language === 'ru' && <ProductVideo id="reports-video" title="Из Excel — в готовый отчёт" description="Загрузка файла, анализ и отчёт с графиками и выводами — в одном ролике." file="reports-20260930" />}
        {/* What you get — output examples */}
        <div className="output-section fade-in-up">
          <h2>{t('output.title')}</h2>
          <p className="section-subtitle">{t('output.subtitle')}</p>
          <div className="output-grid">
            <div className="output-card">
              <div className="output-card-icon"><i className="fas fa-briefcase"></i></div>
              <h3>{t('output.management_title')}</h3>
              <p>{t('output.management_text')}</p>
            </div>
            <div className="output-card">
              <div className="output-card-icon"><i className="fas fa-chart-area"></i></div>
              <h3>{t('output.analytics_title')}</h3>
              <p>{t('output.analytics_text')}</p>
            </div>
            <div className="output-card">
              <div className="output-card-icon"><i className="fas fa-users"></i></div>
              <h3>{t('output.client_title')}</h3>
              <p>{t('output.client_text')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* For whom — target audience (alt background) */}
      <div id="features" className="landing-section-alt">
        <section>
          <div className="audience-section fade-in-up">
            <h2>{t('audience.title')}</h2>
            <p className="section-subtitle">{t('audience.subtitle')}</p>
            <div className="audience-grid">
              <div className="audience-card">
                <div className="audience-icon"><i className="fas fa-user-tie"></i></div>
                <h3>{t('audience.managers_title')}</h3>
                <p>{t('audience.managers_text')}</p>
              </div>
              <div className="audience-card">
                <div className="audience-icon"><i className="fas fa-chart-line"></i></div>
                <h3>{t('audience.analysts_title')}</h3>
                <p>{t('audience.analysts_text')}</p>
              </div>
              <div className="audience-card">
                <div className="audience-icon"><i className="fas fa-bullhorn"></i></div>
                <h3>{t('audience.marketers_title')}</h3>
                <p>{t('audience.marketers_text')}</p>
              </div>
              <div className="audience-card">
                <div className="audience-icon"><i className="fas fa-calculator"></i></div>
                <h3>{t('audience.finance_title')}</h3>
                <p>{t('audience.finance_text')}</p>
              </div>
              <div className="audience-card">
                <div className="audience-icon"><i className="fas fa-building"></i></div>
                <h3>{t('audience.agencies_title')}</h3>
                <p>{t('audience.agencies_text')}</p>
              </div>
            </div>
          </div>

          {/* Before / After */}
          <div className="before-after-section fade-in-up">
            <h2>{t('before_after.title')}</h2>
            <div className="before-after-grid">
              <div className="before-card">
                <div className="ba-label"><i className="fas fa-times-circle"></i> {t('before_after.before_label')}</div>
                <ul>
                  <li><i className="fas fa-table"></i> {t('before_after.before_1')}</li>
                  <li><i className="fas fa-clock"></i> {t('before_after.before_2')}</li>
                  <li><i className="fas fa-copy"></i> {t('before_after.before_3')}</li>
                  <li><i className="fas fa-eye-slash"></i> {t('before_after.before_4')}</li>
                </ul>
              </div>
              <div className="ba-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
              <div className="after-card">
                <div className="ba-label"><i className="fas fa-check-circle"></i> {t('before_after.after_label')}</div>
                <ul>
                  <li><i className="fas fa-file-alt"></i> {t('before_after.after_1')}</li>
                  <li><i className="fas fa-bolt"></i> {t('before_after.after_2')}</li>
                  <li><i className="fas fa-chart-bar"></i> {t('before_after.after_3')}</li>
                  <li><i className="fas fa-lightbulb"></i> {t('before_after.after_4')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section>
        {/* What are ticks — explained clearly */}
        <div className="ticks-explained fade-in-up">
          <div className="ticks-explained-content">
            <h2>{t('credits.title')}</h2>
            <p dangerouslySetInnerHTML={{ __html: t('credits.description') }} />
            <div className="ticks-explained-cards">
              <div className="tick-explain-card">
                <div className="tick-explain-icon"><i className="fas fa-gift"></i></div>
                <div className="tick-explain-value">1000</div>
                <div className="tick-explain-label">{t('credits.free_label')}</div>
              </div>
              <div className="tick-explain-card">
                <div className="tick-explain-icon"><i className="fas fa-file-alt"></i></div>
                <div className="tick-explain-value">100</div>
                <div className="tick-explain-label">{t('credits.per_report_label')}</div>
              </div>
              <div className="tick-explain-card">
                <div className="tick-explain-icon"><i className="fas fa-wallet"></i></div>
                <div className="tick-explain-value">{t('credits.price_value')}</div>
                <div className="tick-explain-label">{t('credits.price_label')}</div>
              </div>
            </div>
          </div>
          <div className="ticks-explained-visual">
            <div className="free-trial-ticks-counter">
              <div className="ticks-icon">
                <i className="fas fa-ticket-alt"></i>
              </div>
              <div className="ticks-count">1000</div>
              <div className="ticks-label">{t('credits.visual_label')}</div>
            </div>
          </div>
        </div>

        {/* Pro Mode */}
        <div className="pro-mode-section fade-in-up">
          <div className="pro-mode-content">
            <div className="pro-mode-badge">
              <i className="fas fa-crown"></i> {t('pro_mode.badge')}
            </div>
            <h2>{t('pro_mode.title')}</h2>
            <p>{t('pro_mode.description')}</p>
            <div className="pro-mode-features">
              <div className="pro-mode-feature">
                <div className="pro-feature-icon"><i className="fas fa-search-plus"></i></div>
                <span>{t('pro_mode.feature_1')}</span>
              </div>
              <div className="pro-mode-feature">
                <div className="pro-feature-icon"><i className="fas fa-database"></i></div>
                <span>{t('pro_mode.feature_2')}</span>
              </div>
              <div className="pro-mode-feature">
                <div className="pro-feature-icon"><i className="fas fa-eye"></i></div>
                <span>{t('pro_mode.feature_3')}</span>
              </div>
              <div className="pro-mode-feature">
                <div className="pro-feature-icon"><i className="fas fa-table"></i></div>
                <span>{t('pro_mode.feature_4')}</span>
              </div>
            </div>
          </div>
          <div className="pro-mode-illustration">
            <div className="pro-mode-visual">
              <div className="pro-visual-icon">
                <i className="fas fa-robot"></i>
              </div>
              <div className="pro-visual-steps">
                <div className="pro-step-line">
                  <i className="fas fa-file-excel"></i>
                  <span>{t('pro_mode.visual_data')}</span>
                </div>
                <div className="pro-step-arrow"><i className="fas fa-arrow-right"></i></div>
                <div className="pro-step-line">
                  <i className="fas fa-database"></i>
                  <span>DuckDB</span>
                </div>
                <div className="pro-step-arrow"><i className="fas fa-arrow-right"></i></div>
                <div className="pro-step-line">
                  <i className="fas fa-chart-bar"></i>
                  <span>{t('pro_mode.visual_report')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Analytics Cards */}
        <div className="custom-cards-section fade-in-up">
          <h2>{t('custom_cards.title')}</h2>
          <p className="section-subtitle">{t('custom_cards.subtitle')}</p>
          <div className="custom-cards-grid">
            <div className="custom-card-example">
              <div className="custom-card-icon"><i className="fas fa-chart-line"></i></div>
              <h3>{t('custom_cards.card1_title')}</h3>
              <p>{t('custom_cards.card1_text')}</p>
            </div>
            <div className="custom-card-example">
              <div className="custom-card-icon"><i className="fas fa-chart-pie"></i></div>
              <h3>{t('custom_cards.card2_title')}</h3>
              <p>{t('custom_cards.card2_text')}</p>
            </div>
            <div className="custom-card-example add-custom">
              <div className="custom-card-icon"><i className="fas fa-plus-circle"></i></div>
              <h3>{t('custom_cards.custom_title')}</h3>
              <p>{t('custom_cards.custom_text')}</p>
            </div>
          </div>
        </div>

        {/* Excel + Export row */}
        <div className="capabilities-row fade-in-up">
          <div className="capability-block">
            <div className="capability-icon"><i className="fas fa-file-excel"></i></div>
            <h3>{t('capabilities.excel_title')}</h3>
            <div className="excel-pills">
              <div className="excel-pill"><i className="fas fa-th"></i> {t('capabilities.excel_merged')}</div>
              <div className="excel-pill"><i className="fas fa-layer-group"></i> {t('capabilities.excel_multi_table')}</div>
              <div className="excel-pill"><i className="fas fa-copy"></i> {t('capabilities.excel_multi_sheet')}</div>
            </div>
          </div>
          <div className="capability-divider"></div>
          <div className="capability-block">
            <div className="capability-icon"><i className="fas fa-download"></i></div>
            <h3>{t('capabilities.export_title')}</h3>
            <div className="export-badges">
              <div className="export-badge word"><i className="fas fa-file-word"></i> Word</div>
              <div className="export-badge pdf"><i className="fas fa-file-pdf"></i> PDF</div>
            </div>
            <p className="capability-note">{t('capabilities.export_note')}</p>
          </div>
          <div className="capability-divider"></div>
          <div className="capability-block">
            <div className="capability-icon"><i className="fas fa-folder-open"></i></div>
            <h3>{t('capabilities.ai_title')}</h3>
            <p className="capability-note">{t('capabilities.ai_note')}</p>
            <div className="folder-ai-badge-small">
              <i className="fas fa-magic"></i> {t('capabilities.ai_badge')}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing — dark accent block */}
      <div id="pricing" className="landing-section-dark">
        <section>
          <div className="pricing-section">
            <div className="pricing-container">
              <div className="pricing-header">
                <h2>{t('pricing.title')}</h2>
                <p>{t('pricing.subtitle')}</p>
              </div>

              <div className="pricing-grid pricing-grid-4">
                {/* Free Editor Plan */}
                <div className="pricing-item pricing-free">
                  <div className="pricing-icon">
                    <i className="fas fa-table"></i>
                  </div>
                  <h3 className="pricing-name">{t('pricing.plan0_name')}</h3>
                  <div className="pricing-price pricing-price-free">
                    {t('pricing.plan0_price')}<span className="currency"></span>
                  </div>
                  <div className="pricing-ticks">{t('pricing.plan0_ticks')}</div>
                  <div className="pricing-reports"></div>
                  <div className="pricing-per-tick"></div>
                  <ul className="pricing-features">
                    <li><i className="fas fa-check"></i> {t('pricing.plan0_f1')}</li>
                    <li><i className="fas fa-check"></i> {t('pricing.plan0_f2')}</li>
                    <li><i className="fas fa-check"></i> {t('pricing.plan0_f3')}</li>
                  </ul>
                  <Link to="/editor" className="pricing-free-cta">{t('pricing.plan0_cta')}</Link>
                </div>

                <div className="pricing-item">
                  <div className="pricing-icon">
                    <i className="fas fa-rocket"></i>
                  </div>
                  <h3 className="pricing-name">{t('pricing.plan1_name')}</h3>
                  <div className="pricing-price">
                    {t('pricing.plan1_price')}<span className="currency">{t('pricing.plan1_currency')}</span>
                  </div>
                  <div className="pricing-ticks">{t('pricing.plan1_ticks')}</div>
                  <div className="pricing-reports">{t('pricing.plan1_reports')}</div>
                  <div className="pricing-per-tick">{t('pricing.plan1_per_tick')}</div>
                  <ul className="pricing-features">
                    <li><i className="fas fa-check"></i> {t('pricing.plan1_f1')}</li>
                    <li><i className="fas fa-check"></i> {t('pricing.plan1_f2')}</li>
                    <li><i className="fas fa-check"></i> {t('pricing.plan1_f3')}</li>
                  </ul>
                </div>

                <div className="pricing-item popular">
                  <div className="pricing-popular-badge">{t('pricing.popular_badge')}</div>
                  <div className="pricing-icon">
                    <i className="fas fa-star"></i>
                  </div>
                  <h3 className="pricing-name">{t('pricing.plan2_name')}</h3>
                  <div className="pricing-price">
                    {t('pricing.plan2_price')}<span className="currency">{t('pricing.plan2_currency')}</span>
                  </div>
                  <div className="pricing-ticks">{t('pricing.plan2_ticks')}</div>
                  <div className="pricing-reports">{t('pricing.plan2_reports')}</div>
                  <div className="pricing-per-tick">{t('pricing.plan2_per_tick')}</div>
                  <ul className="pricing-features">
                    <li><i className="fas fa-check"></i> {t('pricing.plan2_f1')}</li>
                    <li><i className="fas fa-check"></i> {t('pricing.plan2_f2')}</li>
                    <li><i className="fas fa-check"></i> {t('pricing.plan2_f3')}</li>
                  </ul>
                </div>

                <div className="pricing-item">
                  <div className="pricing-icon">
                    <i className="fas fa-crown"></i>
                  </div>
                  <h3 className="pricing-name">{t('pricing.plan3_name')}</h3>
                  <div className="pricing-price">
                    {t('pricing.plan3_price')}<span className="currency">{t('pricing.plan3_currency')}</span>
                  </div>
                  <div className="pricing-ticks">{t('pricing.plan3_ticks')}</div>
                  <div className="pricing-reports">{t('pricing.plan3_reports')}</div>
                  <div className="pricing-per-tick">{t('pricing.plan3_per_tick')}</div>
                  <ul className="pricing-features">
                    <li><i className="fas fa-check"></i> {t('pricing.plan3_f1')}</li>
                    <li><i className="fas fa-check"></i> {t('pricing.plan3_f2')}</li>
                    <li><i className="fas fa-check"></i> {t('pricing.plan3_f3')}</li>
                  </ul>
                </div>
              </div>

              <div className="pricing-cta-row">
                <button onClick={handleLoginClick} className="pricing-cta-btn">
                  <i className="fas fa-rocket"></i>
                  {isAuthenticated ? t('pricing.cta_auth') : t('pricing.cta_guest')}
                </button>
                <span className="pricing-cta-note">{t('pricing.cta_note')}</span>
              </div>

              {!isAuthenticated && (
                <div className="pricing-auth-options">
                  <div className="auth-options">
                    <a href={buildOAuthLoginUrl('google')} className="auth-button">
                      <i className="fab fa-google"></i>
                      {t('pricing.google_btn')}
                    </a>
                    {i18n.language === 'ru' ? (
                      <a href={buildOAuthLoginUrl('yandex')} className="auth-button">
                        <i className="fab fa-yandex"></i>
                        {t('pricing.yandex_btn')}
                      </a>
                    ) : (
                      <Link to="/login" className="auth-button">
                        <i className="fas fa-envelope"></i>
                        {t('pricing.email_btn')}
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <section>
        {/* Testimonials */}
        <div className="testimonials">
          <h2>{t('testimonials.title')}</h2>
          <div className="testimonials-slider">
            <button className="slider-button prev" onClick={prevSlide}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <button className="slider-button next" onClick={nextSlide}>
              <i className="fas fa-chevron-right"></i>
            </button>

            <div className="testimonials-track" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
              {testimonials.map((t, i) => (
                <div className="testimonial-item" key={i}>
                  <i className="fas fa-quote-left"></i>
                  <div className="testimonial-content">
                    <p className="testimonial-text">{t.text}</p>
                    <div className="testimonial-author">
                      <div className="testimonial-author-image">
                        <i className={t.icon}></i>
                      </div>
                      <div className="testimonial-author-info">
                        <div className="testimonial-author-name">{t.name}</div>
                        <div className="testimonial-author-position">{t.position}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="slider-nav">
              {testimonials.map((_, i) => (
                <div
                  key={i}
                  className={`slider-dot ${i === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(i)}
                ></div>
              ))}
            </div>
          </div>
        </div>

        {/* Trust section */}
        <div className="trust-section fade-in-up">
          <h2>{t('trust.title')}</h2>
          <div className="trust-grid">
            <div className="trust-item">
              <i className="fas fa-lock"></i>
              <h3>{t('trust.encryption_title')}</h3>
              <p>{t('trust.encryption_text')}</p>
            </div>
            <div className="trust-item">
              <i className="fas fa-trash-alt"></i>
              <h3>{t('trust.auto_delete_title')}</h3>
              <p>{t('trust.auto_delete_text')}</p>
            </div>
            <div className="trust-item">
              <i className="fas fa-user-shield"></i>
              <h3>{t('trust.privacy_title')}</h3>
              <p>{t('trust.privacy_text')}</p>
            </div>
            <div className="trust-item">
              <i className="fas fa-server"></i>
              <h3>{t('trust.servers_title')}</h3>
              <p>{t('trust.servers_text')}</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="cta">
          <h2>{t('cta.title')}</h2>
          <p>{t('cta.description')}</p>
          <button onClick={handleLoginClick} className="new-report-btn">
            <i className="fas fa-rocket"></i>
            {isAuthenticated ? t('cta.button_auth') : t('cta.button_guest')}
          </button>
          <div className="cta-sub-note">{t('cta.sub_note')}</div>
        </div>

        {/* FAQ */}
        <div id="faq" className="faq">
          <h2>{t('faq.title')}</h2>
          <div className="faq-grid">
            {faqItems.map((item, i) => (
              <div className="faq-item fade-in-up" key={i}>
                <h3><i className="fas fa-question-circle"></i> {item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <img src="/static/logo-ai.png" alt="AI Reports" className="landing-footer-logo" />
            <span className="landing-footer-name">AI Reports</span>
            <p className="landing-footer-tagline">{t('footer.tagline')}</p>
          </div>
          <div className="landing-footer-links">
            <a href="/blog">{t('footer.link_blog')}</a>
            <Link to="/editor">{t('nav.editor')}</Link>
            <Link to="/templates">{t('templates.page_title', 'Templates')}</Link>
            <Link to="/terms">{t('footer.link_terms')}</Link>
            <Link to="/privacy">{t('footer.link_privacy')}</Link>
            <Link to="/contact">{t('footer.link_contact')}</Link>
          </div>
          <div className="landing-footer-copy">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
